import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

export interface CustomerKpis {
  total_customers: number
  active_customers: number
  repeat_rate: number
  avg_ltv: number
  churn_rate: number
}
export interface RfmSegment {
  segment: string
  customers: number
  total_monetary: number
  avg_recency_days: number
}
export interface CohortRow {
  cohort_month: string
  period_index: number
  customers: number
}
export interface TopCustomer {
  customer_id: string
  name: string
  orders: number
  monetary: number
  last_purchase: string
}

export interface ClienteData {
  kpis: CustomerKpis | null
  rfm: RfmSegment[]
  cohort: CohortRow[]
  top: TopCustomer[]
}

// PostgREST devolve colunas numeric como string (precisão). Coage para número.
const num = (v: unknown): number => (v == null ? 0 : Number(v))

export function useClienteAnalytics(windowMonths = 12) {
  const [data, setData] = useState<ClienteData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    const [kpisRes, rfmRes, cohortRes, topRes] = await Promise.all([
      supabase.rpc('fn_customer_kpis', { p_months: windowMonths }),
      supabase.rpc('fn_customer_rfm_segments', { p_months: windowMonths }),
      supabase.rpc('fn_customer_cohort', { p_months: windowMonths }),
      supabase.rpc('fn_customer_top', { p_limit: 10 }),
    ])

    const firstErr = kpisRes.error ?? rfmRes.error ?? cohortRes.error ?? topRes.error
    if (firstErr) setError(firstErr.message)

    const kpiRow = Array.isArray(kpisRes.data) ? kpisRes.data[0] : kpisRes.data

    setData({
      kpis: kpiRow
        ? {
            total_customers: num(kpiRow.total_customers),
            active_customers: num(kpiRow.active_customers),
            repeat_rate: num(kpiRow.repeat_rate),
            avg_ltv: num(kpiRow.avg_ltv),
            churn_rate: num(kpiRow.churn_rate),
          }
        : null,
      rfm: ((rfmRes.data ?? []) as Record<string, unknown>[]).map(r => ({
        segment: String(r.segment),
        customers: num(r.customers),
        total_monetary: num(r.total_monetary),
        avg_recency_days: num(r.avg_recency_days),
      })),
      cohort: ((cohortRes.data ?? []) as Record<string, unknown>[]).map(r => ({
        cohort_month: String(r.cohort_month),
        period_index: num(r.period_index),
        customers: num(r.customers),
      })),
      top: ((topRes.data ?? []) as Record<string, unknown>[]).map(r => ({
        customer_id: String(r.customer_id),
        name: String(r.name ?? ''),
        orders: num(r.orders),
        monetary: num(r.monetary),
        last_purchase: String(r.last_purchase ?? ''),
      })),
    })
    setLoading(false)
  }, [windowMonths])

  useEffect(() => { fetchData() }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}
